const SEARCH_ROOT = "https://api.mapbox.com/search/searchbox/v1";
const COOK_COUNTY_BBOX = "-88.2635,41.4697,-87.5240,42.1543";
const CHICAGO_PROXIMITY = "-87.6298,41.8781";
const DEFAULT_TIMEOUT_MS = 6000;

export interface MapboxSuggestion {
  id: string;
  label: string;
}

export interface MapboxChoice extends MapboxSuggestion {
  latitude: number;
  longitude: number;
}

export interface MapboxSearch {
  suggest(
    query: string,
    sessionToken: string,
    signal: AbortSignal
  ): Promise<MapboxSuggestion[]>;
  retrieve(
    mapboxId: string,
    sessionToken: string,
    signal: AbortSignal
  ): Promise<MapboxChoice[]>;
  forward(query: string, signal: AbortSignal): Promise<MapboxChoice[]>;
}

export class MapboxSearchError extends Error {
  constructor(public readonly reason: "aborted" | "unavailable") {
    super(
      reason === "aborted"
        ? "Address search was cancelled."
        : "Address search is temporarily unavailable."
    );
    this.name = "MapboxSearchError";
  }
}

interface Options {
  token: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

function string(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function label(properties: Record<string, unknown>): string | null {
  const full = string(properties.full_address);
  if (full) return full;
  const name = string(properties.name);
  const place = string(properties.place_formatted);
  if (name && place) return `${name}, ${place}`;
  return name ?? place;
}

function suggestions(value: unknown): MapboxSuggestion[] {
  if (!value || typeof value !== "object") return [];
  const list = (value as { suggestions?: unknown }).suggestions;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const properties = item as Record<string, unknown>;
    const id = string(properties.mapbox_id);
    const display = label(properties);
    return id && display ? [{ id, label: display }] : [];
  });
}

function choices(value: unknown): MapboxChoice[] {
  if (!value || typeof value !== "object") return [];
  const features = (value as { features?: unknown }).features;
  if (!Array.isArray(features)) return [];
  return features.flatMap((feature) => {
    if (!feature || typeof feature !== "object") return [];
    const record = feature as {
      geometry?: { type?: unknown; coordinates?: unknown } | null;
      properties?: unknown;
    };
    if (!record.properties || typeof record.properties !== "object") return [];
    const properties = record.properties as Record<string, unknown>;
    const id = string(properties.mapbox_id);
    const display = label(properties);
    const coordinates = record.geometry?.coordinates;
    if (
      record.geometry?.type !== "Point" ||
      !Array.isArray(coordinates) ||
      coordinates.length < 2
    ) {
      return [];
    }
    const [longitude, latitude] = coordinates;
    if (
      !id ||
      !display ||
      typeof latitude !== "number" ||
      !Number.isFinite(latitude) ||
      latitude < -90 ||
      latitude > 90 ||
      typeof longitude !== "number" ||
      !Number.isFinite(longitude) ||
      longitude < -180 ||
      longitude > 180
    ) {
      return [];
    }
    return [{ id, label: display, latitude, longitude }];
  });
}

function searchParams(token: string): URLSearchParams {
  return new URLSearchParams({
    access_token: token,
    language: "en",
    limit: "5",
    types: "address",
    country: "US",
    proximity: CHICAGO_PROXIMITY,
    bbox: COOK_COUNTY_BBOX,
  });
}

function retrieveParams(token: string): URLSearchParams {
  return new URLSearchParams({
    access_token: token,
    language: "en",
    proximity: CHICAGO_PROXIMITY,
  });
}

export function newMapboxSessionToken(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function createMapboxSearch({
  token,
  fetchImpl,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: Options): MapboxSearch {
  const doFetch =
    fetchImpl ??
    ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));

  async function read(url: string, callerSignal: AbortSignal): Promise<unknown> {
    if (callerSignal.aborted) throw new MapboxSearchError("aborted");
    const controller = new AbortController();
    let timedOut = false;
    const abort = () => controller.abort();
    callerSignal.addEventListener("abort", abort, { once: true });
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    try {
      const response = await doFetch(url, {
        method: "GET",
        credentials: "omit",
        headers: { accept: "application/json" },
        signal: controller.signal,
      });
      if (!response.ok) throw new MapboxSearchError("unavailable");
      return await response.json();
    } catch (cause) {
      if (cause instanceof MapboxSearchError) throw cause;
      if (callerSignal.aborted && !timedOut) {
        throw new MapboxSearchError("aborted");
      }
      throw new MapboxSearchError("unavailable");
    } finally {
      clearTimeout(timer);
      callerSignal.removeEventListener("abort", abort);
    }
  }

  return {
    async suggest(query, sessionToken, signal) {
      const q = query.trim();
      if (!q) return [];
      const params = searchParams(token);
      params.set("q", q);
      params.set("session_token", sessionToken);
      return suggestions(await read(`${SEARCH_ROOT}/suggest?${params}`, signal));
    },
    async retrieve(mapboxId, sessionToken, signal) {
      const params = retrieveParams(token);
      params.set("session_token", sessionToken);
      return choices(
        await read(
          `${SEARCH_ROOT}/retrieve/${encodeURIComponent(mapboxId)}?${params}`,
          signal
        )
      );
    },
    async forward(query, signal) {
      const q = query.trim();
      if (!q) return [];
      const params = searchParams(token);
      params.set("q", q);
      return choices(await read(`${SEARCH_ROOT}/forward?${params}`, signal));
    },
  };
}
